
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();

  const handleReadMore = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50/30 to-orange-50/20 relative">
      <Header />
      
      {/* Subtle background doodles */}
      <div className="fixed inset-0 pointer-events-none opacity-5 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Simple%20line%20art%20doodles%20of%20fruits%20and%20vegetables%20scattered%20pattern%2C%20minimalist%20hand-drawn%20style%2C%20very%20light%20and%20subtle%2C%20apple%20pear%20banana%20carrot%20tomato%2C%20clean%20white%20background%2C%20delicate%20illustration%20for%20web%20design&width=1920&height=1080&seq=blog-bg-doodles&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            variants={heroVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1 
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-gray-800 via-green-600 to-orange-500 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Blog & Insights
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Discover the latest trends, expert insights, and best practices in fresh produce export
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                variants={cardVariants}
                whileHover={{ 
                  y: -12, 
                  scale: 1.02,
                  boxShadow: "0 25px 50px -12px rgba(34, 197, 94, 0.15), 0 0 0 1px rgba(34, 197, 94, 0.1)"
                }}
                whileTap={{ scale: 0.98 }}
                className="bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group border border-gray-100/50"
                onClick={() => handleReadMore(post.slug)}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className="relative overflow-hidden h-56">
                  <motion.img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  />
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  />
                  
                  {/* Category badge */}
                  <motion.div
                    className="absolute top-4 left-4"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <span className="bg-white/90 backdrop-blur-sm text-green-600 px-3 py-1 rounded-full text-sm font-semibold shadow-lg border border-green-100/50">
                      {post.category}
                    </span>
                  </motion.div>
                </div>
                
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <i className="ri-calendar-line mr-2 text-green-500"></i>
                      <span>{post.date}</span>
                    </div>
                    <span className="mx-3">•</span>
                    <div className="flex items-center">
                      <i className="ri-time-line mr-2 text-orange-500"></i>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  
                  <motion.h2 
                    className="text-xl font-bold text-gray-800 mb-4 line-clamp-2 group-hover:text-green-600 transition-colors duration-300"
                    whileHover={{ x: 2 }}
                    transition={{ duration: 0.2 }}
                  >
                    {post.title}
                  </motion.h2>
                  
                  <p className="text-gray-600 text-sm line-clamp-3 mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  
                  <motion.div
                    className="flex items-center justify-between"
                    whileHover={{ x: 2 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-center text-gray-500 text-sm">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-orange-400 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-user-line text-white text-xs"></i>
                      </div>
                      <span>{post.author}</span>
                    </div>
                    
                    <motion.div
                      className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 transition-colors duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Read More
                      <motion.i 
                        className="ri-arrow-right-line ml-2"
                        whileHover={{ x: 3 }}
                        transition={{ duration: 0.2 }}
                      />
                    </motion.div>
                  </motion.div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {/* Load More Button */}
          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <motion.button
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 15px 35px rgba(34, 197, 94, 0.2)"
              }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-10 py-4 rounded-full font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg border border-green-400/20 whitespace-nowrap"
            >
              <span className="flex items-center">
                Load More Articles
                <motion.i 
                  className="ri-refresh-line ml-2"
                  whileHover={{ rotate: 180 }}
                  transition={{ duration: 0.5 }}
                />
              </span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-gradient-to-r from-green-500/10 via-transparent to-orange-500/10 py-20 px-6 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white/60 backdrop-blur-md rounded-3xl p-12 shadow-xl border border-white/20"
          >
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-16 h-16 bg-gradient-to-br from-green-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-6"
              >
                <i className="ri-mail-line text-white text-2xl"></i>
              </motion.div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Stay in the Loop
              </h2>
              <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                Get the latest industry insights, expert tips, and exclusive content delivered straight to your inbox
              </p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <motion.input
                  type="email"
                  placeholder="Enter your email address"
                  whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.1)" }}
                  className="flex-1 px-6 py-4 rounded-full border border-gray-200/50 bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-400/30 focus:outline-none text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-300"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-full font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg whitespace-nowrap"
                >
                  Subscribe Now
                </motion.button>
              </motion.div>
              
              <motion.p 
                className="text-gray-500 text-sm mt-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                No spam, unsubscribe at any time
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
